import { zodResolver } from '@hookform/resolvers/zod';
import { type CustomerAddressInput, CustomerAddressInputSchema } from '@utazzo/shared';
import {
  UzButton,
  UzCombobox,
  UzDialog,
  UzForm,
  UzFormField,
  UzInput,
  UzSelect,
  UzSwitch,
} from '@utazzo/ui';
import { useMemo } from 'react';
import { type Resolver, useForm, useFormContext, useWatch } from 'react-hook-form';

import { useAddAddress, useCountries, useStates } from '../use-customers';

const KIND_OPTIONS = [
  { value: 'HOME', label: 'Home' },
  { value: 'WORK', label: 'Work' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'SHIPPING', label: 'Shipping' },
  { value: 'OTHER', label: 'Other' },
];

const DEFAULTS: CustomerAddressInput = {
  kind: 'HOME',
  isPrimary: false,
  line1: '',
  countryId: '',
  cityName: '',
};

// ---------------------------------------------------------------------------
// Inner fields - read form via context
// ---------------------------------------------------------------------------

const AddressFields = ({ pending }: { pending: boolean }): React.JSX.Element => {
  const { setValue } = useFormContext();
  const countryId = useWatch({ name: 'countryId' }) as string | undefined;
  const countriesQuery = useCountries();
  const statesQuery = useStates(countryId ?? null);

  const countryOptions = useMemo(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countriesQuery.data],
  );

  const stateOptions = useMemo(
    () => (statesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [statesQuery.data],
  );

  const statePlaceholder = !countryId
    ? 'Select a country first'
    : statesQuery.isLoading
      ? 'Loading states…'
      : stateOptions.length === 0
        ? 'No states for this country'
        : 'Select a state (optional)';

  return (
    <div className="space-y-3">
      <UzFormField name="kind">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Address type"
            required
            options={KIND_OPTIONS}
            value={(field.value as string) || 'HOME'}
            onValueChange={field.onChange}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isPrimary">
        {({ field }) => (
          <UzSwitch
            label="Primary address"
            description="Marks this as the default address for this customer."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="line1">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Address line 1"
            placeholder="Building / street"
            required
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="line2">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            label="Address line 2"
            placeholder="Area / locality (optional)"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="landmark">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            label="Landmark"
            placeholder="Optional"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="pincode">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            label="Pincode / ZIP"
            placeholder="Optional"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="countryId">
        {({ field, error }) => (
          <UzCombobox
            name={field.name}
            label="Country"
            required
            placeholder={countriesQuery.isLoading ? 'Loading…' : 'Select a country'}
            searchPlaceholder="Search countries…"
            options={countryOptions}
            value={(field.value as string | undefined) || undefined}
            onValueChange={(v) => {
              field.onChange(v);
              // Clear state when country changes.
              setValue('stateId', undefined, { shouldValidate: false });
              setValue('stateName', null, { shouldValidate: false });
            }}
            loading={countriesQuery.isLoading}
            emptyText="No country found"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="stateId">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="State"
            placeholder={statePlaceholder}
            options={stateOptions}
            value={(field.value as string) || undefined}
            onValueChange={field.onChange}
            error={error}
            disabled={pending || !countryId || statesQuery.isLoading}
          />
        )}
      </UzFormField>
      <UzFormField name="stateName">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            label="State name (free text)"
            placeholder="Use when the state isn't in the list above"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="cityName">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            label="City name"
            placeholder="e.g. Mumbai"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface AddressFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}

export const AddressFormDialog = ({
  open,
  onOpenChange,
  customerId,
}: AddressFormDialogProps): React.JSX.Element => {
  const addAddress = useAddAddress();
  const pending = addAddress.isPending;

  const form = useForm<CustomerAddressInput>({
    // `kind` has a Zod default → resolver input/output diverge; cast to output.
    resolver: zodResolver(CustomerAddressInputSchema) as Resolver<CustomerAddressInput>,
    defaultValues: DEFAULTS,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await addAddress.mutateAsync({ customerId, payload: values });
    form.reset(DEFAULTS);
    onOpenChange(false);
  });

  return (
    <UzDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          form.reset(DEFAULTS);
        }
        onOpenChange(isOpen);
      }}
      title="Add address"
      description="Enter the address details. Country is required; state and city can be free text if not in the lists."
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={onSubmit} loading={pending}>
            Save address
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={onSubmit}>
        <AddressFields pending={pending} />
      </UzForm>
    </UzDialog>
  );
};
